import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermission } from '../lib/permissions';
import { Avatar } from './AppLayout';
import { Clock, CheckSquare, Play, Square, FolderKanban, ListChecks, CheckCircle2, Timer, Flag, Plus, X } from 'lucide-react';
import TaskDetail from '../components/TaskDetail';
import Portal from '../components/Portal';
import { toast } from 'sonner';

function fmtTime(s) { if (!s) return '0h'; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h > 0 ? `${h}h ${m}m` : `${m}m`; }

export default function Dashboard() {
  const { user } = useAuth();
  const { has } = usePermission();
  const canViewCommand = has('dashboard.view_command');
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [tick, setTick] = useState(0);
  const [showAddTask, setShowAddTask] = useState(false);

  const load = async () => {
    const [s, t, p, a] = await Promise.all([
      api.get('/reports/summary'), api.get('/tasks'), api.get('/projects'), api.get('/timer/active'),
    ]);
    setSummary(s.data.summary); setTasks(t.data.tasks); setProjects(p.data.projects); setActive(a.data.active);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const i = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(i); }, []);

  const toggleTimer = async (taskId) => {
    if (active) await api.post('/timer/stop');
    if (!active || active.task_id !== taskId) await api.post('/timer/start', { task_id: taskId });
    load();
  };

  const myTasks = tasks.filter(t => (t.assignees || []).some(a => a.id === user.id) && !t.completed).slice(0, 6);
  const myOpenTasksCount = tasks.filter(t => (t.assignees || []).some(a => a.id === user.id) && !t.completed).length;
  const completedByMeCount = tasks.filter(t => (t.assignees || []).some(a => a.id === user.id) && t.completed).length;
  const overdueByMeCount = tasks.filter(t => (t.assignees || []).some(a => a.id === user.id) && !t.completed && t.due_date && new Date(t.due_date) < new Date()).length;
  const activeProjectsCount = projects.length;

  const getStatusCount = (status) => {
    return tasks.filter(t => (t.assignees || []).some(a => a.id === user.id) && t.task_status === status).length;
  };

  // "Team progress" widget: show for anyone managing at least one project (delegator)
  const managedProjects = projects.filter((p) => p.delegator_id === user.id).map((p) => {
    const pt = tasks.filter((t) => t.project_id === p.id);
    const done = pt.filter((t) => t.completed).length;
    const overdue = pt.filter((t) => !t.completed && t.due_date && new Date(t.due_date) < new Date()).length;
    return { ...p, total: pt.length, done, overdue, pct: pt.length ? Math.round((done / pt.length) * 100) : 0 };
  });

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999);
  const dueToday = tasks.filter(t => !t.completed && t.due_date && (t.assignees || []).some(a => a.id === user.id)
    && new Date(t.due_date).setHours(23, 59, 59, 999) <= todayEnd.getTime());
  const upcoming = tasks.filter(t => !t.completed && t.due_date && (t.assignees || []).some(a => a.id === user.id)
    && new Date(t.due_date) > today && new Date(t.due_date) <= new Date(today.getTime() + 7 * 86400000)).slice(0, 6);
  const activeElapsed = active ? Math.floor((Date.now() - new Date(active.started_at).getTime()) / 1000) : 0;

  const cardStyle = {
    background: 'var(--trello-card-bg)',
    border: '1px solid var(--trello-border)',
    borderRadius: 16,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
    boxShadow: 'var(--trello-shadow)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  };

  const iconContainerStyle = {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  };

  const numberStyle = {
    fontSize: 24,
    fontWeight: 800,
    color: 'var(--trello-text)',
    lineHeight: 1
  };

  const labelStyle = {
    fontSize: 12,
    color: 'var(--trello-text-muted)',
    marginTop: 4,
    fontWeight: 600
  };

  const myTotalTasks = tasks.filter(t => (t.assignees || []).some(a => a.id === user.id));
  const myTotalCount = myTotalTasks.length;

  const statuses = [
    { key: 'backlog', label: 'Backlog', color: '#94a3b8' },
    { key: 'todo', label: 'To do', color: '#3b82f6' },
    { key: 'in_progress', label: 'In progress', color: '#ff3b20' }, // Bexcode Red
    { key: 'in_review', label: 'In review', color: '#a855f7' },
    { key: 'done', label: 'Done', color: '#10b981' }
  ];

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="dashboard">
      <style>{`
        .db-metric-card {
          background: var(--trello-card-bg);
          border: 1px solid var(--trello-border);
          border-radius: 16px;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          box-shadow: var(--trello-shadow);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .db-metric-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03);
          border-color: var(--trello-blue);
        }
        .db-timer-pulse {
          animation: timerPulse 2s infinite ease-in-out;
        }
        @keyframes timerPulse {
          0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(97, 189, 79, 0.4); }
          70% { transform: scale(1.05); opacity: 0.8; box-shadow: 0 0 0 10px rgba(97, 189, 79, 0); }
          100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(97, 189, 79, 0); }
        }
        .db-status-bar-segment {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }
        .db-status-bar-segment:hover {
          opacity: 0.85;
          filter: brightness(1.1);
        }
        .db-timeline-card {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .db-timeline-item:hover .db-timeline-card {
          transform: translateX(4px);
          border-color: var(--trello-blue);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        }
        .db-proj-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .db-proj-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.08);
          border-color: var(--trello-blue);
        }
      `}</style>

      {/* Header Panel */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, color: 'var(--trello-text)' }}>
            Welcome back, {user.full_name?.split(' ')[0]}
          </h1>
          <p style={{ opacity: 0.85, marginTop: 4, color: 'var(--trello-text-muted)', fontSize: 14 }}>
            Here's what's happening across your workspace today.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {(user.role === 'leader' || user.role === 'manager') && (
            <button
              onClick={() => setShowAddTask(true)}
              className="tm-btn"
              style={{
                padding: '8px 16px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 700,
                background: 'var(--trello-blue, #0079BF)',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              data-testid="new-task-dashboard-btn"
            >
              <Plus size={16} /> New Task
            </button>
          )}
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--trello-text-muted)', background: 'var(--trello-hover)', padding: '6px 14px', borderRadius: 20, border: '1px solid var(--trello-border)' }}>
            📅 {new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Active Timer running widget */}
      {active && (
        <div className="tm-stat-card mb-6 db-timer-pulse" style={{ borderLeftColor: '#61BD4F', borderLeftWidth: 4, display: 'flex', alignItems: 'center', gap: 16, background: 'var(--trello-card-bg)', boxShadow: '0 4px 20px rgba(97, 189, 79, 0.1)' }} data-testid="active-timer">
          <div style={{ background: '#61BD4F', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Clock size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timer running</div>
            <div style={{ fontWeight: 700, color: 'var(--trello-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{active.task_title}</div>
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--trello-text)', marginRight: 8 }} data-testid="timer-elapsed">{fmtTime(activeElapsed)}</div>
          <button className="tm-btn" style={{ background: '#DE350B', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8 }} onClick={() => toggleTimer(active.task_id)} data-testid="stop-active-timer">
            <Square size={12} fill="#fff" /> Stop
          </button>
        </div>
      )}

      {/* Summary Cards Grid */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="summary-cards">
          {/* Active Projects */}
          <div className="db-metric-card" onClick={() => nav('/projects')} data-testid="stat-projects-card">
            <div style={{ ...iconContainerStyle, background: 'rgba(255, 59, 32, 0.08)', color: 'var(--trello-blue)' }}>
              <FolderKanban size={20} />
            </div>
            <div>
              <div style={numberStyle} data-testid="stat-projects">{activeProjectsCount}</div>
              <div style={labelStyle}>Active projects</div>
            </div>
          </div>

          {/* Open Tasks */}
          <div className="db-metric-card" onClick={() => nav(canViewCommand ? '/command-center' : '/projects')} data-testid="stat-tasks-card">
            <div style={{ ...iconContainerStyle, background: 'rgba(59, 130, 246, 0.08)', color: '#3b82f6' }}>
              <Clock size={20} />
            </div>
            <div>
              <div style={numberStyle} data-testid="stat-tasks">{myOpenTasksCount}</div>
              <div style={labelStyle}>Your open tasks</div>
            </div>
          </div>

          {/* Completed Tasks */}
          <div className="db-metric-card" onClick={() => nav(canViewCommand ? '/command-center?status=done' : '/projects')} data-testid="stat-completed-card">
            <div style={{ ...iconContainerStyle, background: 'rgba(16, 185, 129, 0.08)', color: '#10b981' }}>
              <CheckCircle2 size={20} />
            </div>
            <div>
              <div style={numberStyle} data-testid="stat-completed">{completedByMeCount}</div>
              <div style={labelStyle}>Completed by you</div>
            </div>
          </div>

          {/* Overdue Tasks */}
          <div className="db-metric-card" onClick={() => nav(canViewCommand ? '/command-center?status=overdue' : '/projects')} data-testid="stat-time-card">
            <div style={{ ...iconContainerStyle, background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444' }}>
              <Flag size={20} />
            </div>
            <div>
              <div style={numberStyle} data-testid="stat-time">{overdueByMeCount}</div>
              <div style={labelStyle}>Overdue tasks</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Double Column Layout */}
      <div style={{ display: 'flex', gap: 24, flexDirection: 'row', flexWrap: 'wrap', marginBottom: 32 }}>
        {/* Left Column: Agenda & Projects */}
        <div style={{ flex: '1 1 500px', display: 'flex', flexDirection: 'column', gap: 32 }}>
          {/* Due Soon Agenda */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--trello-text)', margin: '0 0 16px 0' }}>Due soon</h3>
            {dueToday.length === 0 && upcoming.length === 0 ? (
              <div style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 16, padding: '40px 24px', textAlign: 'center', boxShadow: 'var(--trello-shadow)' }}>
                <span style={{ color: 'var(--trello-text-muted)', fontSize: 13, fontWeight: 600 }}>✨ All caught up! Nothing due in the next 7 days.</span>
              </div>
            ) : (
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 16, paddingLeft: 8 }}>
                <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: 'var(--trello-border)' }} />
                {[...dueToday, ...upcoming].slice(0, 5).map(t => {
                  const isOverdue = t.due_date && new Date(t.due_date) < new Date() && !t.completed;
                  const isToday = t.due_date && new Date(t.due_date).setHours(0, 0, 0, 0) === today.getTime();

                  let dateLabel = new Date(t.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  let dateColor = 'var(--trello-text-muted)';
                  let dateBg = 'var(--trello-hover)';

                  if (isOverdue) {
                    dateLabel = 'Overdue';
                    dateColor = '#ef4444';
                    dateBg = 'rgba(239, 68, 68, 0.1)';
                  } else if (isToday) {
                    dateLabel = 'Today';
                    dateColor = '#f59e0b';
                    dateBg = 'rgba(245, 158, 11, 0.1)';
                  }

                  return (
                    <div key={t.id} className="db-timeline-item" style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1, cursor: 'pointer' }} onClick={() => setOpenTaskId(t.id)}>
                      <div style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        background: 'var(--trello-card-bg)',
                        border: `3px solid ${isOverdue ? '#ef4444' : isToday ? '#f59e0b' : 'var(--trello-blue)'}`,
                        flexShrink: 0
                      }} />
                      <div className="db-timeline-card" style={{ flex: 1, background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--trello-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                          <span style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 2 }}>{t.project_name || 'General Task'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <span className={`tm-pill tm-pill-${t.priority_v2 || t.priority || 'medium'}`} style={{ fontSize: 10, padding: '2px 8px' }}>{t.priority_v2 || t.priority || 'medium'}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: dateColor, background: dateBg, padding: '3px 8px', borderRadius: 6 }}>{dateLabel}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Your Projects */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--trello-text)', margin: '0 0 16px 0' }}>Your projects</h3>
            {projects.length === 0 ? (
              <div style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: 'var(--trello-shadow)' }}>
                <p style={{ color: 'var(--trello-text-muted)', fontSize: 13, margin: 0, fontWeight: 600 }}>No projects assigned yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {projects.slice(0, 6).map(p => (
                  <Link to={`/projects/${p.id}`} key={p.id} data-testid={`dashboard-project-${p.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="db-proj-card" style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: 'var(--trello-shadow)', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: p.color || 'var(--trello-blue)' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--trello-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        {p.delegator_name && <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginTop: 4, fontWeight: 600 }}>Manager: {p.delegator_name}</div>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Status Analytics & Team Progress */}
        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 32 }}>
          {/* Status Analytics */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--trello-text)', margin: '0 0 16px 0' }}>Your task status</h3>
            <div style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 16, padding: 24, boxShadow: 'var(--trello-shadow)' }}>

              {/* Stacked Distribution Progress Bar */}
              <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: 'var(--trello-border)', marginBottom: 20 }}>
                {statuses.map(s => {
                  const count = myTotalTasks.filter(t => t.task_status === s.key).length;
                  const pct = myTotalCount > 0 ? (count / myTotalCount) * 100 : 0;
                  if (pct === 0) return null;
                  return (
                    <div key={s.key}
                      className="db-status-bar-segment"
                      style={{
                        width: `${pct}%`,
                        background: s.color,
                        height: '100%'
                      }}
                      title={`${s.label}: ${count} (${Math.round(pct)}%)`}
                    />
                  );
                })}
              </div>

              {/* Status List details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {statuses.map(s => {
                  const count = myTotalTasks.filter(t => t.task_status === s.key).length;
                  const pct = myTotalCount > 0 ? Math.round((count / myTotalCount) * 100) : 0;
                  return (
                    <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifySpace: 'between', fontSize: 13, fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--trello-text)' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                        <span style={{ color: 'var(--trello-text-muted)' }}>{s.label}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        <span style={{ color: 'var(--trello-text)', fontWeight: 700 }}>{count}</span>
                        <span style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontWeight: 500 }}>({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {myOpenTasksCount === 0 && (
                <div style={{ borderTop: '1px solid var(--trello-border)', marginTop: 16, paddingTop: 16, textAlign: 'center', fontSize: 12, color: 'var(--trello-text-muted)', fontWeight: 600 }}>
                  No active tasks assigned to you.
                </div>
              )}
            </div>
          </div>

          {/* Team Progress Tracker */}
          {managedProjects.length > 0 && (
            <div data-testid="manager-team-progress">
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--trello-text)', margin: '0 0 16px 0' }}>Team progress — my projects</h3>
              <div style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 16, padding: 24, boxShadow: 'var(--trello-shadow)', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {managedProjects.map((p) => (
                  <Link key={p.id} to={`/projects/${p.id}`} data-testid={`mgr-proj-${p.id}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || 'var(--trello-blue)', flexShrink: 0 }} />
                          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--trello-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                          {p.overdue > 0 && <span className="tm-pill tm-pill-critical" style={{ fontSize: 9, padding: '1px 6px', flexShrink: 0 }}>{p.overdue} overdue</span>}
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--trello-text-muted)', fontWeight: 700, flexShrink: 0 }}>{p.done}/{p.total} done</span>
                      </div>
                      <div className="tm-progress-track" style={{ width: '100%', height: 6, borderRadius: 3, background: 'var(--trello-border)', overflow: 'hidden' }}>
                        <div className="tm-progress-fill" style={{ width: `${p.pct}%`, background: p.color || 'var(--trello-blue)', height: '100%', borderRadius: 3 }} />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {openTaskId && (
        <TaskDetail taskId={openTaskId} currentUser={user}
          onClose={() => setOpenTaskId(null)} onChange={load} />
      )}

      {showAddTask && (
        <AddTaskModal
          projects={projects}
          onClose={() => setShowAddTask(false)}
          onSaved={() => {
            setShowAddTask(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function AddTaskModal({ projects, onClose, onSaved }) {
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !projectId) return;
    setBusy(true);
    try {
      await api.post('/tasks', {
        project_id: projectId,
        title: title.trim(),
        description: description,
        dueDate: dueDate || null,
      });
      toast.success('Task created successfully!', {
        style: {
          background: '#10b981',
          color: '#fff',
          borderColor: '#10b981'
        }
      });
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create task', {
        style: {
          background: '#ef4444',
          color: '#fff',
          borderColor: '#ef4444'
        }
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Portal>
      <div className="tm-modal-overlay" onClick={onClose}>
        <div className="tm-modal" onClick={(e) => e.stopPropagation()} data-testid="add-task-modal"
          style={{ maxWidth: 520, padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

          {/* Modal Header */}
          <div style={{
            padding: '20px 24px 24px',
            background: 'linear-gradient(135deg, var(--trello-blue, #0079BF) 0%, rgba(0, 121, 191, 0.8) 100%)',
            color: '#fff', position: 'relative',
          }}>
            <button onClick={onClose} data-testid="close-add-task"
              style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 999, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={16} />
            </button>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.8, fontWeight: 700 }}>New Task</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 0', letterSpacing: -0.3 }}>
              Add Task to Board
            </h2>
          </div>

          <form onSubmit={submit} style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
            {/* Project Dropdown */}
            <div style={{ marginBottom: 16 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>PROJECT NAME</label>
              <select data-testid="task-project-select" className="tm-input"
                value={projectId} onChange={(e) => setProjectId(e.target.value)} required
                style={{ fontSize: 14, padding: '10px 12px' }}>
                <option value="">-- Select Project --</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Task Name */}
            <div style={{ marginBottom: 16 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>TASK NAME</label>
              <input data-testid="task-name-input" className="tm-input"
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Design homepage layout" required autoFocus
                style={{ fontSize: 15, padding: '10px 12px' }} />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 18 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>DESCRIPTION</label>
              <textarea data-testid="task-description-textarea" className="tm-input" rows={3}
                value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="What needs to be done? (optional)"
                style={{ fontSize: 14, padding: '10px 12px', resize: 'vertical' }} />
            </div>

            {/* End Date */}
            <div style={{ marginBottom: 18 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>END DATE</label>
              <input type="date" data-testid="task-due-date-input" className="tm-input"
                value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                style={{ fontSize: 14, padding: '10px 12px' }} />
            </div>
          </form>

          {/* Sticky action bar */}
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--trello-border)', background: 'var(--trello-card-bg)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="tm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" onClick={submit} className="tm-btn" disabled={busy || !title.trim() || !projectId} data-testid="create-task-submit"
              style={{ background: 'var(--trello-blue, #0079BF)', padding: '10px 20px', fontWeight: 700, letterSpacing: 0.2 }}>
              {busy ? 'Creating…' : 'Create task'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
